// BusinessIntelligenceScreen.js
// ─────────────────────────────────────────────────────────────────────────────
// JobEng Business Intelligence — full-featured analytics dashboard
//
//   • Period selector: 7d / 30d / 90d / This Quarter / This Year / Custom range
//   • Real trend % calculated by comparing current period to previous period
//   • KPI cards with sparklines, targets, progress bars, drill-down
//   • Goals / targets per KPI — persisted to AsyncStorage
//   • Auto-generated insights ("Revenue up X% vs last period", etc.)
//   • Pure-RN visualisations (no chart library):
//       - Sparkline (mini bar series)
//       - Funnel chart (pipeline + recruitment)
//       - Revenue-by-month bar chart
//       - Invoice aging buckets
//       - Top customers ranking
//       - Department headcount breakdown
//   • 4 distinct tabs: Dashboard / KPIs / Reports / Trends
//   • Drill-down modal showing source records per KPI
//   • Pre-built reports with tap-through detail view
//   • Search, filter (department/status), auto-refresh
//   • Share / export via native Share sheet
//   • Multi-currency from company profile
//   • Cross-company safe — every API call scoped by company_id
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions,
  ScrollView, RefreshControl, StatusBar, Alert, Modal, ActivityIndicator,
  TextInput, Switch, Share,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');

// ── Brand ────────────────────────────────────────────────────────────────────
const PRIMARY  = '#0e4e96';
const ACCENT   = '#7C3AED';
const SUCCESS  = '#10B981';
const WARNING  = '#F59E0B';
const DANGER   = '#EF4444';
const INFO     = '#3B82F6';
const NEUTRAL  = '#6B7280';
const SURFACE  = '#FFFFFF';
const BG       = '#F3F4F6';
const BORDER   = '#E5E7EB';

// ── API ──────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY },
});

api.interceptors.request.use(async (c) => {
  try {
    const t = (await AsyncStorage.getItem('auth_token'))
      || (await AsyncStorage.getItem('userToken'))
      || (await AsyncStorage.getItem('token'));
    if (t) c.headers.Authorization = `Bearer ${t}`;
  } catch {}
  return c;
});

// Canonical endpoints (matches existing JobEng conventions)
const EP = {
  companyProfile: (id) => `/api/company-profile/${id}`,
  deals:        '/api/data/crm_deals',
  contacts:     '/api/data/crm_contacts',
  activities:   '/api/data/crm_activities',
  stages:       '/api/data/crm_stages',
  feedback:     '/api/data/customer_feedback',
  employees:    '/api/employees',
  clients:      '/api/clients',
  invoices:     '/api/invoices',
  contracts:    '/api/contracts',
  campaigns:    '/api/campaigns',
  vendors:      '/api/vendors',
  documents:    '/api/documents',
  jobs:         '/api/jobs',
  applications: '/api/applications',
  interviews:   '/api/interviews',
  offers:       '/api/offers',
  projects:     '/api/projects',
  teamMembers:  '/api/team/members',
};

const safeData = (r) =>
  (r?.data?.success && Array.isArray(r.data.data)) ? r.data.data :
  Array.isArray(r?.data) ? r.data :
  Array.isArray(r?.data?.data) ? r.data.data : [];

const getBusinessId = async () => {
  try {
    return (await AsyncStorage.getItem('company_id')) || '0';
  } catch { return '0'; }
};

// ── Period helpers ───────────────────────────────────────────────────────────
const PERIODS = [
  { key: '7d',      label: '7 days',     days: 7 },
  { key: '30d',     label: '30 days',    days: 30 },
  { key: '90d',     label: '90 days',    days: 90 },
  { key: 'quarter', label: 'Quarter',    days: 90 },
  { key: 'year',    label: 'Year',       days: 365 },
  { key: 'custom',  label: 'Custom',     days: null },
];

const dayMs = 86400000;

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const getPeriodRange = (period, customStart, customEnd) => {
  const now = new Date();
  if (period === 'custom' && customStart && customEnd) {
    return { start: startOfDay(new Date(customStart)), end: endOfDay(new Date(customEnd)) };
  }
  if (period === 'quarter') {
    const m = now.getMonth();
    const qStart = new Date(now.getFullYear(), Math.floor(m / 3) * 3, 1);
    return { start: startOfDay(qStart), end: endOfDay(now) };
  }
  if (period === 'year') {
    const yStart = new Date(now.getFullYear(), 0, 1);
    return { start: startOfDay(yStart), end: endOfDay(now) };
  }
  const def = PERIODS.find(p => p.key === period);
  const days = def?.days || 30;
  return { start: startOfDay(new Date(now.getTime() - days * dayMs)), end: endOfDay(now) };
};

const getPreviousPeriodRange = ({ start, end }) => {
  const length = end.getTime() - start.getTime();
  return {
    start: new Date(start.getTime() - length - dayMs),
    end:   new Date(start.getTime() - dayMs),
  };
};

const pickDate = (obj) =>
  obj.paid_at || obj.payment_date || obj.invoice_date || obj.closed_at ||
  obj.applied_at || obj.hired_at || obj.interview_date || obj.offer_date ||
  obj.start_date || obj.created_at || obj.createdAt || obj.date || obj.timestamp;

const inRange = (raw, start, end) => {
  if (!raw) return false;
  const t = new Date(raw).getTime();
  if (isNaN(t)) return false;
  return t >= start.getTime() && t <= end.getTime();
};

// ── Number / currency formatters ────────────────────────────────────────────
const formatNum = (v) => {
  if (v == null || isNaN(v)) return '0';
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'k';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const pctChange = (current, previous) => {
  if (previous === 0 || previous == null) return current > 0 ? 100 : 0;
  return ((current - previous) / Math.abs(previous)) * 100;
};

// ── AsyncStorage goals (per-company) ─────────────────────────────────────────
const GOALS_KEY = (cid) => `bi_goals_${cid}`;

const DEFAULT_GOALS = {
  revenue:       10000,
  pipeline:      50000,
  newClients:    5,
  applications:  50,
  hires:         3,
  invoicesPaid:  10,
};

// ════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

// Pure-RN sparkline using vertical bars
const Sparkline = ({ data, color = PRIMARY, height: h = 30, width: w = 80 }) => {
  if (!data || data.length === 0) {
    return <View style={{ height: h, width: w }} />;
  }
  const max = Math.max(...data, 1);
  const barW = Math.max(1, (w - (data.length - 1) * 2) / data.length);
  return (
    <View style={{ height: h, width: w, flexDirection: 'row', alignItems: 'flex-end' }}>
      {data.map((v, i) => (
        <View key={i} style={{
          width: barW,
          marginRight: i === data.length - 1 ? 0 : 2,
          height: Math.max(2, (v / max) * h),
          backgroundColor: color,
          opacity: 0.4 + 0.6 * (i / data.length),
          borderRadius: 1,
        }} />
      ))}
    </View>
  );
};

// Horizontal bar with label + value (used in lists, funnels)
const BarRow = ({ label, value, total, color = PRIMARY, suffix = '', max }) => {
  const cap = max != null ? max : (total || 1);
  const pct = Math.min(100, Math.max(0, (value / (cap || 1)) * 100));
  return (
    <View style={st.barRow}>
      <Text style={st.barLbl} numberOfLines={1}>{label}</Text>
      <View style={st.barBg}>
        <View style={[st.barFill, { width: `${pct}%`, backgroundColor: color }]} />
      </View>
      <Text style={st.barCnt}>{typeof value === 'number' ? formatNum(value) : value}{suffix}</Text>
    </View>
  );
};

// Funnel stage — bar that gets narrower as you go down
const FunnelStage = ({ label, value, max, color, indent = 0 }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const barW = Math.max(20, pct);
  return (
    <View style={{ marginBottom: 8, paddingLeft: indent }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
        <Text style={{ fontSize: 13, color: '#1F2937', fontWeight: '600' }}>{label}</Text>
        <Text style={{ fontSize: 13, color: NEUTRAL }}>{formatNum(value)}</Text>
      </View>
      <View style={{ height: 14, borderRadius: 7, backgroundColor: '#F3F4F6', overflow: 'hidden' }}>
        <LinearGradient
          colors={[color, color + 'CC']}
          start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
          style={{ width: `${barW}%`, height: '100%', borderRadius: 7 }}
        />
      </View>
    </View>
  );
};

// Donut / circular progress (approximation via thick border circle)
const DonutProgress = ({ value, target, color = PRIMARY, size = 60 }) => {
  const pct = target > 0 ? Math.min(100, (value / target) * 100) : 0;
  const ringColor = pct >= 100 ? SUCCESS : pct >= 66 ? color : pct >= 33 ? WARNING : DANGER;
  return (
    <View style={{ width: size, height: size, position: 'relative', alignItems: 'center', justifyContent: 'center' }}>
      <View style={{
        width: size, height: size, borderRadius: size / 2,
        borderWidth: 5, borderColor: BORDER,
      }} />
      <View style={{
        position: 'absolute', width: size, height: size, borderRadius: size / 2,
        borderWidth: 5,
        borderTopColor: ringColor,
        borderRightColor: pct > 25 ? ringColor : BORDER,
        borderBottomColor: pct > 50 ? ringColor : BORDER,
        borderLeftColor: pct > 75 ? ringColor : BORDER,
        transform: [{ rotate: '-45deg' }],
      }} />
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={{ fontSize: 12, fontWeight: '800', color: ringColor }}>{Math.round(pct)}%</Text>
      </View>
    </View>
  );
};

// KPI card with title, value, trend, sparkline, target ring
const KPICard = ({
  title, value, type, trend, sparkData, target, color = PRIMARY, icon, onPress, currencyFormat,
}) => {
  const display = type === 'currency' ? currencyFormat(value) : formatNum(value);
  const trendUp = trend > 0;
  const trendNeutral = Math.abs(trend) < 0.5;
  return (
    <TouchableOpacity activeOpacity={0.7} onPress={onPress} style={st.kpiCard}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 }}>
        <View style={[st.kpiIcon, { backgroundColor: color + '15' }]}>
          <MaterialCommunityIcons name={icon} size={18} color={color} />
        </View>
        <View style={{ flex: 1, marginLeft: 10 }}>
          <Text style={st.kpiTitle} numberOfLines={1}>{title}</Text>
          <Text style={[st.kpiValue, { color }]} numberOfLines={1}>{display}</Text>
        </View>
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <MaterialCommunityIcons
            name={trendNeutral ? 'minus' : trendUp ? 'arrow-up' : 'arrow-down'}
            size={12}
            color={trendNeutral ? NEUTRAL : trendUp ? SUCCESS : DANGER}
          />
          <Text style={{
            fontSize: 11, fontWeight: '700',
            color: trendNeutral ? NEUTRAL : trendUp ? SUCCESS : DANGER,
          }}>
            {trendNeutral ? '—' : `${trendUp ? '+' : ''}${trend.toFixed(1)}%`}
          </Text>
          <Text style={{ fontSize: 10, color: NEUTRAL }}>vs prev</Text>
        </View>
        <Sparkline data={sparkData} color={color} height={22} width={60} />
      </View>

      {target > 0 && (
        <View style={{ marginTop: 8 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
            <Text style={{ fontSize: 10, color: NEUTRAL }}>
              Target: {type === 'currency' ? currencyFormat(target) : formatNum(target)}
            </Text>
            <Text style={{ fontSize: 10, fontWeight: '700', color: value >= target ? SUCCESS : color }}>
              {Math.round(Math.min(100, (value / target) * 100))}%
            </Text>
          </View>
          <View style={{ height: 4, backgroundColor: BORDER, borderRadius: 2, overflow: 'hidden' }}>
            <View style={{
              width: `${Math.min(100, (value / target) * 100)}%`,
              height: '100%',
              backgroundColor: value >= target ? SUCCESS : color,
              borderRadius: 2,
            }} />
          </View>
        </View>
      )}
    </TouchableOpacity>
  );
};

const InsightCard = ({ icon, color, text, severity }) => (
  <View style={[st.insightCard, { borderLeftColor: color }]}>
    <View style={[st.insightIcon, { backgroundColor: color + '15' }]}>
      <MaterialCommunityIcons name={icon} size={16} color={color} />
    </View>
    <Text style={st.insightText} numberOfLines={3}>{text}</Text>
    {severity === 'high' && (
      <View style={[st.severityDot, { backgroundColor: color }]} />
    )}
  </View>
);

// Period chip selector
const PeriodChip = ({ active, label, onPress, icon }) => (
  <TouchableOpacity
    onPress={onPress}
    style={[st.periodChip, active && { backgroundColor: PRIMARY, borderColor: PRIMARY }]}
    activeOpacity={0.7}>
    {icon && <MaterialCommunityIcons name={icon} size={13} color={active ? '#FFF' : NEUTRAL} />}
    <Text style={{
      fontSize: 12, fontWeight: '600',
      color: active ? '#FFF' : '#374151',
      marginLeft: icon ? 4 : 0,
    }}>{label}</Text>
  </TouchableOpacity>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function BusinessIntelligenceScreen({ navigation, route }) {
  // ── Core state ─────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading]     = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [activeTab, setActiveTab]     = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [companyId, setCompanyId]     = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // ── Period / comparison ────────────────────────────────────────────────────
  const [period, setPeriod]           = useState('30d');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd]     = useState(null);
  const [showPeriodPicker, setShowPeriodPicker] = useState(false);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showFilters, setShowFilters]           = useState(false);

  // ── Modals ─────────────────────────────────────────────────────────────────
  const [selected, setSelected]             = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [activeReport, setActiveReport]     = useState(null);
  const [drilldownRows, setDrilldownRows]   = useState(null);
  const [drilldownTitle, setDrilldownTitle] = useState('');

  // ── Goals ──────────────────────────────────────────────────────────────────
  const [goals, setGoals] = useState(DEFAULT_GOALS);
  const [goalEditing, setGoalEditing] = useState(null);

  // ── Raw data caches ────────────────────────────────────────────────────────
  const [data, setData] = useState({
    invoices: [], deals: [], contacts: [], activities: [], stages: [],
    employees: [], clients: [], applications: [], jobs: [],
    interviews: [], offers: [], projects: [], contracts: [],
    campaigns: [], vendors: [], feedback: [],
  });

  const autoRefreshTimer = useRef(null);

  // ── Currency formatter (memoised per company) ──────────────────────────────
  const formatCurrency = useCallback((v) => {
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: companyProfile?.currency || 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(v || 0);
    } catch {
      return `${companyProfile?.currency || 'GBP'} ${formatNum(v)}`;
    }
  }, [companyProfile]);

  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  // ════════════════════════════════════════════════════════════════════════
  // BOOTSTRAP & DATA FETCH
  // ════════════════════════════════════════════════════════════════════════
  useEffect(() => { init(); }, []);

  useEffect(() => {
    if (companyId) loadGoals();
  }, [companyId]);

  useEffect(() => {
    if (autoRefresh && companyId) {
      autoRefreshTimer.current = setInterval(() => fetchAll(companyId), 60000); // 1 min
      return () => { if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current); };
    }
    if (autoRefreshTimer.current) { clearInterval(autoRefreshTimer.current); autoRefreshTimer.current = null; }
  }, [autoRefresh, companyId]);

  const init = async () => {
    try {
      setIsLoading(true);
      const b = route?.params?.company_id || await getBusinessId();
      setCompanyId(b);
      await fetchAll(b);
    } catch (e) {
      console.warn('BI init failed:', e?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try { await fetchAll(companyId); }
    finally { setRefreshing(false); }
  };

  const loadGoals = async () => {
    try {
      const raw = await AsyncStorage.getItem(GOALS_KEY(companyId));
      if (raw) setGoals({ ...DEFAULT_GOALS, ...JSON.parse(raw) });
    } catch {}
  };

  const saveGoals = async (next) => {
    try {
      setGoals(next);
      await AsyncStorage.setItem(GOALS_KEY(companyId), JSON.stringify(next));
    } catch {}
  };

  const fetchAll = async (bizId) => {
    const cid = parseInt(bizId) || 0;
    if (cid === 0) {
      console.warn('BI: no valid company_id, skipping fetch');
      return;
    }
    const p = { company_id: cid };
    const ep = EP;

    const requests = [
      api.get(ep.companyProfile(cid)).catch(() => null),
      api.get(ep.invoices,     { params: p }).catch(() => null),
      api.get(ep.deals,        { params: p }).catch(() => null),
      api.get(ep.contacts,     { params: p }).catch(() => null),
      api.get(ep.activities,   { params: p }).catch(() => null),
      api.get(ep.stages,       { params: p }).catch(() => null),
      api.get(ep.employees,    { params: p }).catch(() => null),
      api.get(ep.clients,      { params: p }).catch(() => null),
      api.get(ep.applications, { params: p }).catch(() => null),
      api.get(ep.jobs,         { params: p }).catch(() => null),
      api.get(ep.interviews,   { params: p }).catch(() => null),
      api.get(ep.offers,       { params: p }).catch(() => null),
      api.get(ep.projects,     { params: p }).catch(() => null),
      api.get(ep.contracts,    { params: p }).catch(() => null),
      api.get(ep.campaigns,    { params: p }).catch(() => null),
      api.get(ep.vendors,      { params: p }).catch(() => null),
      api.get(ep.feedback,     { params: p }).catch(() => null),
    ];

    const results = await Promise.allSettled(requests);
    const get = (i) => results[i]?.status === 'fulfilled' ? results[i].value : null;

    // Profile
    const profileRes = get(0);
    if (profileRes?.data?.success) {
      const profile = Array.isArray(profileRes.data.data) ? profileRes.data.data[0] : profileRes.data.data;
      if (profile) {
        setCompanyProfile(profile);
        try { await AsyncStorage.setItem('company_profile', JSON.stringify(profile)); } catch {}
      }
    } else {
      try {
        const c = await AsyncStorage.getItem('company_profile');
        if (c) setCompanyProfile(JSON.parse(c));
      } catch {}
    }

    setData({
      invoices:     safeData(get(1)),
      deals:        safeData(get(2)),
      contacts:     safeData(get(3)),
      activities:   safeData(get(4)),
      stages:       safeData(get(5)),
      employees:    safeData(get(6)),
      clients:      safeData(get(7)),
      applications: safeData(get(8)),
      jobs:         safeData(get(9)),
      interviews:   safeData(get(10)),
      offers:       safeData(get(11)),
      projects:     safeData(get(12)),
      contracts:    safeData(get(13)),
      campaigns:    safeData(get(14)),
      vendors:      safeData(get(15)),
      feedback:     safeData(get(16)),
    });

    setLastUpdated(new Date());
  };

  // ════════════════════════════════════════════════════════════════════════
  // PERIOD-AWARE COMPUTATIONS
  // ════════════════════════════════════════════════════════════════════════
  const periodRange = useMemo(
    () => getPeriodRange(period, customStart, customEnd),
    [period, customStart, customEnd]
  );

  const prevPeriodRange = useMemo(
    () => getPreviousPeriodRange(periodRange),
    [periodRange]
  );

  // Filter helper — given an array, return items dated within a range
  const within = useCallback((arr, range) => {
    return (arr || []).filter(item => inRange(pickDate(item), range.start, range.end));
  }, []);

  // Sparkline buckets — split a range into ~10 buckets and count values
  const buildSparkline = useCallback((arr, range, valueFn = () => 1) => {
    const buckets = 10;
    const len = range.end.getTime() - range.start.getTime();
    const step = len / buckets;
    const out = new Array(buckets).fill(0);
    arr.forEach(item => {
      const t = new Date(pickDate(item) || 0).getTime();
      if (isNaN(t) || t < range.start.getTime() || t > range.end.getTime()) return;
      const idx = Math.min(buckets - 1, Math.max(0, Math.floor((t - range.start.getTime()) / step)));
      out[idx] += Number(valueFn(item)) || 0;
    });
    return out;
  }, []);

  // ── KPI computations ─────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const { invoices, deals, employees, clients, applications, jobs, contracts } = data;

    // Revenue (paid invoices in period)
    const paidInPeriod = within(invoices.filter(i => i.status === 'paid'), periodRange);
    const paidPrev     = within(invoices.filter(i => i.status === 'paid'), prevPeriodRange);
    const revenueNow   = paidInPeriod.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0);
    const revenuePrev  = paidPrev.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0);

    // Pipeline (deals not yet closed)
    const openDeals    = deals.filter(d => !['closed_won', 'closed_lost', 'lost', 'won'].includes(d.status || d.stage));
    const pipelineNow  = openDeals.reduce((s, d) => s + parseFloat(d.value || d.amount || 0), 0);
    const pipelinePrev = openDeals
      .filter(d => inRange(d.created_at, prevPeriodRange.start, prevPeriodRange.end))
      .reduce((s, d) => s + parseFloat(d.value || d.amount || 0), 0);

    // New clients (added in period)
    const newClientsNow  = within(clients, periodRange).length;
    const newClientsPrev = within(clients, prevPeriodRange).length;

    // Applications received in period
    const appsNow  = within(applications, periodRange).length;
    const appsPrev = within(applications, prevPeriodRange).length;

    // Hires
    const hiresNow  = within(applications.filter(a => a.status === 'hired'), periodRange).length;
    const hiresPrev = within(applications.filter(a => a.status === 'hired'), prevPeriodRange).length;

    // Invoices paid count
    const paidCountNow  = paidInPeriod.length;
    const paidCountPrev = paidPrev.length;

    // Active jobs
    const activeJobs = jobs.filter(j => j.status === 'active' || j.status === 'open').length;
    const activeJobsPrev = jobs.filter(j =>
      (j.status === 'active' || j.status === 'open') &&
      inRange(j.created_at, prevPeriodRange.start, prevPeriodRange.end)
    ).length;

    // Active contracts
    const activeContracts = contracts.filter(c => c.status === 'active' || c.status === 'valid').length;

    return [
      {
        id: 'revenue', title: 'Revenue', value: revenueNow, prev: revenuePrev,
        type: 'currency', icon: 'cash-multiple', color: SUCCESS,
        spark: buildSparkline(paidInPeriod, periodRange, (i) => parseFloat(i.total || i.amount || 0)),
        target: goals.revenue, drillSource: 'invoices', drillFilter: { status: 'paid', dated: true },
      },
      {
        id: 'pipeline', title: 'Pipeline', value: pipelineNow, prev: pipelinePrev,
        type: 'currency', icon: 'chart-bell-curve-cumulative', color: PRIMARY,
        spark: buildSparkline(openDeals, periodRange, (d) => parseFloat(d.value || d.amount || 0)),
        target: goals.pipeline, drillSource: 'deals',
      },
      {
        id: 'newClients', title: 'New Clients', value: newClientsNow, prev: newClientsPrev,
        type: 'number', icon: 'account-multiple-plus', color: INFO,
        spark: buildSparkline(within(clients, periodRange), periodRange),
        target: goals.newClients, drillSource: 'clients', drillFilter: { dated: true },
      },
      {
        id: 'applications', title: 'Applications', value: appsNow, prev: appsPrev,
        type: 'number', icon: 'file-document-multiple', color: ACCENT,
        spark: buildSparkline(within(applications, periodRange), periodRange),
        target: goals.applications, drillSource: 'applications', drillFilter: { dated: true },
      },
      {
        id: 'hires', title: 'Hires', value: hiresNow, prev: hiresPrev,
        type: 'number', icon: 'account-check', color: SUCCESS,
        spark: buildSparkline(within(applications.filter(a => a.status === 'hired'), periodRange), periodRange),
        target: goals.hires, drillSource: 'applications', drillFilter: { status: 'hired', dated: true },
      },
      {
        id: 'invoicesPaid', title: 'Invoices Paid', value: paidCountNow, prev: paidCountPrev,
        type: 'number', icon: 'receipt', color: INFO,
        spark: buildSparkline(paidInPeriod, periodRange),
        target: goals.invoicesPaid, drillSource: 'invoices', drillFilter: { status: 'paid', dated: true },
      },
      {
        id: 'activeJobs', title: 'Active Jobs', value: activeJobs, prev: activeJobsPrev,
        type: 'number', icon: 'briefcase-search', color: WARNING,
        spark: buildSparkline(jobs.filter(j => j.status === 'active' || j.status === 'open'), periodRange),
        target: 0, drillSource: 'jobs', drillFilter: { status: ['active', 'open'] },
      },
      {
        id: 'employees', title: 'Headcount', value: data.employees.length, prev: data.employees.length,
        type: 'number', icon: 'account-group', color: PRIMARY,
        spark: buildSparkline(data.employees, periodRange),
        target: 0, drillSource: 'employees',
      },
      {
        id: 'contracts', title: 'Active Contracts', value: activeContracts, prev: activeContracts,
        type: 'number', icon: 'file-sign', color: NEUTRAL,
        spark: buildSparkline(contracts.filter(c => c.status === 'active'), periodRange),
        target: 0, drillSource: 'contracts', drillFilter: { status: 'active' },
      },
    ].map(k => ({ ...k, trend: pctChange(k.value, k.prev) }));
  }, [data, periodRange, prevPeriodRange, goals, within, buildSparkline]);

  // ── Insights (auto-derived) ───────────────────────────────────────────────
  const insights = useMemo(() => {
    const out = [];
    const rev = kpis.find(k => k.id === 'revenue');
    const pipe = kpis.find(k => k.id === 'pipeline');
    const apps = kpis.find(k => k.id === 'applications');
    const hires = kpis.find(k => k.id === 'hires');
    const newClients = kpis.find(k => k.id === 'newClients');

    if (rev && rev.prev > 0) {
      const change = rev.trend;
      if (change > 10) out.push({
        icon: 'trending-up', color: SUCCESS,
        text: `Revenue up ${change.toFixed(1)}% vs previous period — strong growth`,
        severity: 'normal',
      });
      else if (change < -10) out.push({
        icon: 'trending-down', color: DANGER,
        text: `Revenue down ${Math.abs(change).toFixed(1)}% vs previous period — needs attention`,
        severity: 'high',
      });
    }

    if (pipe && pipe.value > 0) {
      out.push({
        icon: 'cash-fast', color: PRIMARY,
        text: `Open pipeline worth ${formatCurrency(pipe.value)} across ${data.deals.filter(d => !['closed_won','closed_lost','lost','won'].includes(d.status || d.stage)).length} deals`,
        severity: 'normal',
      });
    }

    // Overdue invoices
    const now = new Date();
    const overdue = data.invoices.filter(i =>
      i.status !== 'paid' && i.due_date && new Date(i.due_date) < now
    );
    if (overdue.length > 0) {
      const overdueTotal = overdue.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0);
      out.push({
        icon: 'alert-circle', color: DANGER,
        text: `${overdue.length} overdue invoice${overdue.length === 1 ? '' : 's'} totalling ${formatCurrency(overdueTotal)}`,
        severity: 'high',
      });
    }

    if (apps && hires && apps.value > 0) {
      const rate = (hires.value / apps.value) * 100;
      out.push({
        icon: 'target', color: INFO,
        text: `Recruitment conversion: ${rate.toFixed(1)}% (${hires.value} hires from ${apps.value} applications)`,
        severity: 'normal',
      });
    }

    if (newClients && newClients.value === 0 && newClients.prev > 0) {
      out.push({
        icon: 'account-alert', color: WARNING,
        text: `No new clients in this period — last period had ${newClients.prev}`,
        severity: 'high',
      });
    }

    // Top performing client
    if (data.invoices.length > 0) {
      const byClient = {};
      data.invoices.filter(i => i.status === 'paid').forEach(i => {
        const k = i.client_name || i.client || `Client ${i.client_id || '?'}`;
        byClient[k] = (byClient[k] || 0) + parseFloat(i.total || i.amount || 0);
      });
      const top = Object.entries(byClient).sort((a, b) => b[1] - a[1])[0];
      if (top && top[1] > 0) {
        out.push({
          icon: 'crown', color: '#D97706',
          text: `Top client: ${top[0]} (${formatCurrency(top[1])})`,
          severity: 'normal',
        });
      }
    }

    // Expiring contracts
    const expiring = data.contracts.filter(c => {
      if (!c.end_date) return false;
      const d = new Date(c.end_date);
      const days = (d.getTime() - now.getTime()) / dayMs;
      return days > 0 && days <= 30;
    });
    if (expiring.length > 0) {
      out.push({
        icon: 'clock-alert', color: WARNING,
        text: `${expiring.length} contract${expiring.length === 1 ? '' : 's'} expiring in the next 30 days`,
        severity: 'high',
      });
    }

    return out;
  }, [kpis, data, formatCurrency]);

  // ── Reports computations ─────────────────────────────────────────────────
  const reports = useMemo(() => {
    const { invoices, deals, stages, jobs, applications, interviews, offers,
            clients, employees, contracts } = data;

    // Revenue by month (last 12 months)
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59);
      const monthlyRev = invoices
        .filter(inv => inv.status === 'paid')
        .filter(inv => {
          const t = new Date(pickDate(inv) || 0).getTime();
          return t >= d.getTime() && t <= end.getTime();
        })
        .reduce((s, inv) => s + parseFloat(inv.total || inv.amount || 0), 0);
      months.push({
        label: d.toLocaleDateString('en-GB', { month: 'short' }),
        value: monthlyRev,
      });
    }
    const totalAnnualRev = months.reduce((s, m) => s + m.value, 0);

    // Pipeline funnel by stage
    const stageMap = {};
    deals.forEach(d => {
      const stage = d.stage || d.status || 'unknown';
      stageMap[stage] = stageMap[stage] || { count: 0, value: 0 };
      stageMap[stage].count += 1;
      stageMap[stage].value += parseFloat(d.value || d.amount || 0);
    });
    const STAGE_ORDER = ['new', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];
    const pipelineFunnel = Object.entries(stageMap)
      .sort((a, b) => STAGE_ORDER.indexOf(a[0]) - STAGE_ORDER.indexOf(b[0]))
      .map(([stage, v]) => ({ label: stage.replace(/_/g, ' '), count: v.count, value: v.value }));

    // Recruitment funnel
    const recFunnel = [
      { label: 'Open Jobs',     value: jobs.filter(j => j.status === 'active' || j.status === 'open').length, color: PRIMARY },
      { label: 'Applications',  value: applications.length, color: INFO },
      { label: 'Interviews',    value: interviews.length, color: ACCENT },
      { label: 'Offers',        value: offers.length, color: WARNING },
      { label: 'Hires',         value: applications.filter(a => a.status === 'hired').length, color: SUCCESS },
    ];

    // Top clients by revenue
    const byClient = {};
    invoices.filter(i => i.status === 'paid').forEach(i => {
      const k = i.client_name || i.client || `Client #${i.client_id || '?'}`;
      byClient[k] = (byClient[k] || 0) + parseFloat(i.total || i.amount || 0);
    });
    const topClients = Object.entries(byClient)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    // Invoice aging
    const aging = { current: 0, '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
    const agingCount = { current: 0, '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
    const today = new Date();
    invoices.forEach(i => {
      if (i.status === 'paid') return;
      const total = parseFloat(i.total || i.amount || 0);
      if (!i.due_date) { aging.current += total; agingCount.current += 1; return; }
      const days = Math.floor((today.getTime() - new Date(i.due_date).getTime()) / dayMs);
      const bucket = days <= 0 ? 'current'
        : days <= 30 ? '0-30'
        : days <= 60 ? '31-60'
        : days <= 90 ? '61-90'
        : '90+';
      aging[bucket] += total;
      agingCount[bucket] += 1;
    });

    // Department headcount
    const byDept = {};
    employees.forEach(e => {
      const k = e.department || 'Unassigned';
      byDept[k] = (byDept[k] || 0) + 1;
    });
    const deptList = Object.entries(byDept).map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Contract status
    const contractStatus = {
      active: contracts.filter(c => c.status === 'active' || c.status === 'valid').length,
      expiring: contracts.filter(c => {
        if (!c.end_date) return false;
        const d = new Date(c.end_date);
        const days = (d.getTime() - today.getTime()) / dayMs;
        return days > 0 && days <= 30;
      }).length,
      expired: contracts.filter(c => c.status === 'expired' ||
        (c.end_date && new Date(c.end_date) < today)).length,
      draft: contracts.filter(c => c.status === 'draft').length,
    };

    return {
      revenueByMonth: { months, total: totalAnnualRev },
      pipelineFunnel,
      recFunnel,
      topClients,
      aging: { values: aging, counts: agingCount },
      deptList,
      contractStatus,
    };
  }, [data]);

  // ── Drilldown ───────────────────────────────────────────────────────────
  const openDrilldown = (kpi) => {
    let rows = [];
    const ds = kpi.drillSource;
    const arr = data[ds] || [];
    if (kpi.drillFilter) {
      const f = kpi.drillFilter;
      rows = arr.filter(item => {
        if (f.status) {
          if (Array.isArray(f.status)) {
            if (!f.status.includes(item.status)) return false;
          } else if (item.status !== f.status) return false;
        }
        if (f.dated && !inRange(pickDate(item), periodRange.start, periodRange.end)) return false;
        return true;
      });
    } else {
      rows = arr;
    }
    setDrilldownRows(rows);
    setDrilldownTitle(kpi.title);
  };

  const closeDrilldown = () => {
    setDrilldownRows(null);
    setDrilldownTitle('');
  };

  // ── Export / share ──────────────────────────────────────────────────────
  const exportShare = async () => {
    const periodLabel = PERIODS.find(p => p.key === period)?.label || period;
    const lines = [];
    lines.push(`📊 ${companyProfile?.name || 'Business'} — BI Report`);
    lines.push(`Period: ${periodLabel} (${formatDate(periodRange.start)} → ${formatDate(periodRange.end)})`);
    lines.push('');
    lines.push('━━━ KEY METRICS ━━━');
    kpis.forEach(k => {
      const val = k.type === 'currency' ? formatCurrency(k.value) : formatNum(k.value);
      const trend = isFinite(k.trend) ? `${k.trend >= 0 ? '+' : ''}${k.trend.toFixed(1)}%` : '—';
      lines.push(`${k.title}: ${val} (${trend} vs prev)`);
    });
    lines.push('');
    if (insights.length > 0) {
      lines.push('━━━ INSIGHTS ━━━');
      insights.forEach(i => lines.push(`• ${i.text}`));
    }
    try {
      await Share.share({
        message: lines.join('\n'),
        title: `${companyProfile?.name || 'Business'} BI Report`,
      });
    } catch {}
  };

  // ── Filters ─────────────────────────────────────────────────────────────
  const departments = useMemo(() => {
    const set = new Set();
    data.employees.forEach(e => { if (e.department) set.add(e.department); });
    return ['all', ...Array.from(set)];
  }, [data.employees]);

  // ── Search results across all data ──────────────────────────────────────
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    const out = [];
    const scan = (arr, type, nameField) => {
      arr.forEach(item => {
        const name = item[nameField] || item.name || item.title || `#${item.id}`;
        if (JSON.stringify(item).toLowerCase().includes(q)) {
          out.push({ ...item, _type: type, _displayName: name });
        }
      });
    };
    scan(data.invoices, 'invoice', 'invoice_number');
    scan(data.deals, 'deal', 'name');
    scan(data.clients, 'client', 'name');
    scan(data.employees, 'employee', 'name');
    scan(data.applications, 'application', 'candidate_name');
    scan(data.jobs, 'job', 'title');
    scan(data.contracts, 'contract', 'title');
    return out.slice(0, 50);
  }, [searchQuery, data]);

  // ════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════
  // (continues in part 2 below)
  // ...

  // ── Header ──────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={[ACCENT, '#8B5CF6']} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={st.hBtn}>
          <MaterialCommunityIcons name="arrow-left" size={22} color="#FFF" />
        </TouchableOpacity>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text style={st.hTitle} numberOfLines={1}>{companyProfile?.name || 'Business Intelligence'}</Text>
          <Text style={st.hSub}>
            {companyProfile?.industry ? `${companyProfile.industry} · ` : ''}Business Intelligence
          </Text>
        </View>
        <TouchableOpacity onPress={exportShare} style={st.hBtn}>
          <MaterialCommunityIcons name="share-variant" size={20} color="#FFF" />
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setShowGoalsModal(true)} style={[st.hBtn, { marginLeft: 8 }]}>
          <MaterialCommunityIcons name="target" size={20} color="#FFF" />
        </TouchableOpacity>
      </View>
      <View style={st.chips}>
        {companyProfile?.headquarters && (
          <View style={st.chip}>
            <MaterialCommunityIcons name="map-marker" size={11} color="#DDD6FE" />
            <Text style={st.chipTxt} numberOfLines={1}>{companyProfile.headquarters}</Text>
          </View>
        )}
        {companyProfile?.employee_count && (
          <View style={st.chip}>
            <MaterialCommunityIcons name="account-group" size={11} color="#DDD6FE" />
            <Text style={st.chipTxt}>{companyProfile.employee_count} team</Text>
          </View>
        )}
        {lastUpdated && (
          <View style={st.chip}>
            <MaterialCommunityIcons name="clock-outline" size={11} color="#DDD6FE" />
            <Text style={st.chipTxt}>
              Updated {lastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </View>
        )}
        <View style={[st.chip, { backgroundColor: autoRefresh ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.08)' }]}>
          <Switch
            value={autoRefresh}
            onValueChange={setAutoRefresh}
            thumbColor={autoRefresh ? SUCCESS : '#fff'}
            trackColor={{ true: 'rgba(16,185,129,0.5)', false: 'rgba(255,255,255,0.3)' }}
            style={{ transform: [{ scaleX: 0.65 }, { scaleY: 0.65 }], marginRight: -4 }}
          />
          <Text style={st.chipTxt}>Auto</Text>
        </View>
      </View>
    </LinearGradient>
  );

  // ── Period bar ──────────────────────────────────────────────────────────
  const renderPeriodBar = () => (
    <View style={st.periodBar}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 12, gap: 6, alignItems: 'center' }}>
        {PERIODS.map(p => (
          <PeriodChip
            key={p.key}
            label={p.label}
            active={period === p.key}
            onPress={() => {
              if (p.key === 'custom') setShowPeriodPicker(true);
              setPeriod(p.key);
            }}
          />
        ))}
        <View style={{ width: 1, height: 20, backgroundColor: BORDER, marginHorizontal: 6 }} />
        <Text style={{ fontSize: 11, color: NEUTRAL }}>
          {formatDate(periodRange.start)} – {formatDate(periodRange.end)}
        </Text>
      </ScrollView>
    </View>
  );

  // ── Tabs ────────────────────────────────────────────────────────────────
  const renderTabs = () => {
    const tabs = [
      { k: 'dashboard', t: 'Dashboard', i: 'view-dashboard' },
      { k: 'kpis',      t: 'KPIs',      i: 'target' },
      { k: 'reports',   t: 'Reports',   i: 'file-chart' },
      { k: 'trends',    t: 'Trends',    i: 'trending-up' },
    ];
    return (
      <View style={st.tabsRow}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingHorizontal: 12 }}>
          {tabs.map(tab => (
            <TouchableOpacity key={tab.k}
              style={[st.tab, activeTab === tab.k && st.activeTab]}
              onPress={() => setActiveTab(tab.k)}>
              <MaterialCommunityIcons name={tab.i} size={16}
                color={activeTab === tab.k ? PRIMARY : NEUTRAL} />
              <Text style={[st.tabTxt, activeTab === tab.k && { color: PRIMARY, fontWeight: '700' }]}>
                {tab.t}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  // ── Search bar ──────────────────────────────────────────────────────────
  const renderSearchBar = () => (
    <View style={st.searchRow}>
      <MaterialCommunityIcons name="magnify" size={18} color="#9CA3AF" />
      <TextInput
        style={st.searchInput}
        placeholder="Search invoices, deals, clients…"
        placeholderTextColor="#9CA3AF"
        value={searchQuery}
        onChangeText={setSearchQuery}
        returnKeyType="search"
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons name="close-circle" size={16} color="#9CA3AF" />
        </TouchableOpacity>
      )}
    </View>
  );

  // ── Search results ──────────────────────────────────────────────────────
  const renderSearchResults = () => (
    <View>
      <Text style={st.sectionTitle}>Search Results ({searchResults.length})</Text>
      {searchResults.length === 0 ? (
        <View style={st.empty}>
          <MaterialCommunityIcons name="magnify" size={40} color="#D1D5DB" />
          <Text style={st.emptyTxt}>No matches for "{searchQuery}"</Text>
        </View>
      ) : (
        searchResults.map((item, i) => (
          <TouchableOpacity key={i} style={st.card}
            onPress={() => { setSelected(item); setShowDetailModal(true); }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={[st.typeBadge, { backgroundColor: PRIMARY + '15' }]}>
                <Text style={{ fontSize: 10, fontWeight: '800', color: PRIMARY }}>
                  {String(item._type ?? '').toUpperCase().slice(0, 4)}
                </Text>
              </View>
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={st.cardName} numberOfLines={1}>{item._displayName}</Text>
                <Text style={st.cardMeta} numberOfLines={1}>
                  {item.status || item.stage || item.email || item.department || '—'}
                </Text>
              </View>
              {parseFloat(item.total || item.value || item.amount || 0) > 0 && (
                <Text style={st.cardVal}>{formatCurrency(item.total || item.value || item.amount)}</Text>
              )}
            </View>
          </TouchableOpacity>
        ))
      )}
    </View>
  );

  // ── Dashboard tab ───────────────────────────────────────────────────────
  const renderDashboard = () => {
    const topKPIs = kpis.slice(0, 4);
    const highlightedKPI = kpis.reduce((best, k) =>
      Math.abs(k.trend) > Math.abs(best?.trend || 0) ? k : best, null);

    return (
      <View>
        {/* Top KPI grid (2 columns) */}
        <Text style={st.sectionTitle}>Key Metrics</Text>
        <View style={st.kpiGrid}>
          {topKPIs.map(k => (
            <View key={k.id} style={st.kpiGridItem}>
              <KPICard {...k} currencyFormat={formatCurrency} onPress={() => openDrilldown(k)} />
            </View>
          ))}
        </View>

        {/* Insights */}
        {insights.length > 0 && (
          <>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
              marginTop: 8, paddingHorizontal: 16 }}>
              <Text style={st.sectionTitle}>Insights</Text>
              <View style={{ backgroundColor: PRIMARY + '15', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2 }}>
                <Text style={{ fontSize: 11, fontWeight: '700', color: PRIMARY }}>{insights.length}</Text>
              </View>
            </View>
            <View style={{ paddingHorizontal: 16 }}>
              {insights.map((ins, i) => <InsightCard key={i} {...ins} />)}
            </View>
          </>
        )}

        {/* Highlighted change */}
        {highlightedKPI && Math.abs(highlightedKPI.trend) > 5 && (
          <View style={{ paddingHorizontal: 16, marginTop: 16 }}>
            <Text style={st.sectionTitle}>Biggest Movement</Text>
            <View style={[st.highlightCard, { borderColor: highlightedKPI.trend > 0 ? SUCCESS : DANGER }]}>
              <View style={[st.kpiIcon, { backgroundColor: highlightedKPI.color + '15' }]}>
                <MaterialCommunityIcons name={highlightedKPI.icon} size={24} color={highlightedKPI.color} />
              </View>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={{ fontSize: 13, color: NEUTRAL }}>{highlightedKPI.title}</Text>
                <Text style={{ fontSize: 20, fontWeight: '800', color: '#1F2937' }}>
                  {highlightedKPI.type === 'currency' ? formatCurrency(highlightedKPI.value) : formatNum(highlightedKPI.value)}
                </Text>
                <Text style={{ fontSize: 12, color: highlightedKPI.trend > 0 ? SUCCESS : DANGER, fontWeight: '700' }}>
                  {highlightedKPI.trend > 0 ? '↑' : '↓'} {Math.abs(highlightedKPI.trend).toFixed(1)}% vs previous
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* Quick links to reports */}
        <Text style={st.sectionTitle}>Quick Reports</Text>
        <View style={{ paddingHorizontal: 16, flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {[
            { k: 'revenue',     i: 'chart-line',         label: 'Revenue Trend',  color: SUCCESS },
            { k: 'pipeline',    i: 'filter-variant',     label: 'Sales Pipeline', color: PRIMARY },
            { k: 'recruitment', i: 'account-search',     label: 'Hiring Funnel',  color: ACCENT },
            { k: 'aging',       i: 'calendar-alert',     label: 'Invoice Aging',  color: WARNING },
          ].map(r => (
            <TouchableOpacity key={r.k}
              style={[st.quickReport, { width: (width - 40) / 2 }]}
              onPress={() => { setActiveReport(r.k); setShowReportModal(true); }}>
              <View style={[st.kpiIcon, { backgroundColor: r.color + '15' }]}>
                <MaterialCommunityIcons name={r.i} size={18} color={r.color} />
              </View>
              <Text style={{ fontSize: 13, fontWeight: '600', color: '#1F2937', marginTop: 8 }}>
                {r.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  };

  // ── KPIs tab ────────────────────────────────────────────────────────────
  const renderKPIsTab = () => (
    <View>
      <Text style={st.sectionTitle}>All KPIs ({kpis.length})</Text>
      <View style={st.kpiGrid}>
        {kpis.map(k => (
          <View key={k.id} style={st.kpiGridItem}>
            <KPICard {...k} currencyFormat={formatCurrency} onPress={() => openDrilldown(k)} />
          </View>
        ))}
      </View>
    </View>
  );

  // ── Reports tab ─────────────────────────────────────────────────────────
  const renderReportsTab = () => {
    const reportList = [
      { k: 'revenue',     title: 'Revenue by Month', icon: 'chart-bar',         color: SUCCESS,
        subtitle: `${formatCurrency(reports.revenueByMonth.total)} over 12 months` },
      { k: 'pipeline',    title: 'Sales Pipeline',   icon: 'filter-variant',    color: PRIMARY,
        subtitle: `${reports.pipelineFunnel.length} stages tracked` },
      { k: 'recruitment', title: 'Hiring Funnel',    icon: 'account-search',    color: ACCENT,
        subtitle: `${reports.recFunnel[1].value} applications → ${reports.recFunnel[4].value} hires` },
      { k: 'topClients',  title: 'Top Clients',      icon: 'crown',             color: '#D97706',
        subtitle: `Top ${reports.topClients.length} ranked by revenue` },
      { k: 'aging',       title: 'Invoice Aging',    icon: 'calendar-alert',    color: WARNING,
        subtitle: `${Object.values(reports.aging.counts).reduce((s, n) => s + n, 0)} unpaid invoices` },
      { k: 'departments', title: 'Headcount by Dept',icon: 'office-building',   color: INFO,
        subtitle: `${reports.deptList.length} departments` },
      { k: 'contracts',   title: 'Contract Status',  icon: 'file-sign',         color: NEUTRAL,
        subtitle: `${reports.contractStatus.active} active, ${reports.contractStatus.expiring} expiring` },
    ];

    return (
      <View>
        <Text style={st.sectionTitle}>Pre-built Reports</Text>
        {reportList.map(r => (
          <TouchableOpacity key={r.k} style={st.reportCard}
            onPress={() => { setActiveReport(r.k); setShowReportModal(true); }}>
            <View style={[st.kpiIcon, { backgroundColor: r.color + '15' }]}>
              <MaterialCommunityIcons name={r.icon} size={22} color={r.color} />
            </View>
            <View style={{ flex: 1, marginLeft: 14 }}>
              <Text style={st.reportTitle}>{r.title}</Text>
              <Text style={st.reportSubtitle}>{r.subtitle}</Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={22} color="#9CA3AF" />
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  // ── Trends tab ──────────────────────────────────────────────────────────
  const renderTrendsTab = () => {
    const maxMonth = Math.max(1, ...reports.revenueByMonth.months.map(m => m.value));
    return (
      <View>
        <Text style={st.sectionTitle}>Revenue (12 months)</Text>
        <View style={st.chartCard}>
          <View style={{ height: 140, flexDirection: 'row', alignItems: 'flex-end', gap: 4 }}>
            {reports.revenueByMonth.months.map((m, i) => {
              const h = (m.value / maxMonth) * 130;
              const isMax = m.value === maxMonth && m.value > 0;
              return (
                <View key={i} style={{ flex: 1, alignItems: 'center' }}>
                  <View style={{
                    width: '80%', height: Math.max(2, h), borderRadius: 4,
                    backgroundColor: isMax ? SUCCESS : PRIMARY,
                    opacity: 0.5 + 0.5 * (i / reports.revenueByMonth.months.length),
                  }} />
                </View>
              );
            })}
          </View>
          <View style={{ height: 1, backgroundColor: BORDER, marginVertical: 8 }} />
          <View style={{ flexDirection: 'row', gap: 4 }}>
            {reports.revenueByMonth.months.map((m, i) => (
              <Text key={i} style={{ flex: 1, fontSize: 9, color: NEUTRAL, textAlign: 'center' }}>
                {m.label}
              </Text>
            ))}
          </View>
          <Text style={{ textAlign: 'center', fontSize: 12, color: NEUTRAL, marginTop: 8 }}>
            12-month total: <Text style={{ fontWeight: '800', color: '#1F2937' }}>
              {formatCurrency(reports.revenueByMonth.total)}
            </Text>
          </Text>
        </View>

        <Text style={st.sectionTitle}>Application Trend</Text>
        <View style={st.chartCard}>
          {(() => {
            const months = [];
            const now = new Date();
            for (let i = 5; i >= 0; i--) {
              const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
              const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59);
              const c = data.applications.filter(a => {
                const t = new Date(pickDate(a) || 0).getTime();
                return t >= d.getTime() && t <= end.getTime();
              }).length;
              months.push({ label: d.toLocaleDateString('en-GB', { month: 'short' }), value: c });
            }
            const max = Math.max(1, ...months.map(m => m.value));
            return (
              <View>
                {months.map((m, i) => (
                  <BarRow key={i} label={m.label} value={m.value} max={max} color={ACCENT} />
                ))}
              </View>
            );
          })()}
        </View>

        <Text style={st.sectionTitle}>Activity Volume</Text>
        <View style={st.chartCard}>
          {(() => {
            const byType = {};
            data.activities.forEach(a => {
              const k = a.activity_type || a.type || 'other';
              byType[k] = (byType[k] || 0) + 1;
            });
            const entries = Object.entries(byType).sort((a, b) => b[1] - a[1]).slice(0, 6);
            const max = Math.max(1, ...entries.map(([, v]) => v));
            if (entries.length === 0) {
              return (
                <View style={{ alignItems: 'center', padding: 20 }}>
                  <MaterialCommunityIcons name="chart-bar-stacked" size={32} color="#D1D5DB" />
                  <Text style={{ color: NEUTRAL, marginTop: 8, fontSize: 13 }}>No activity data</Text>
                </View>
              );
            }
            return entries.map(([type, count], i) => (
              <BarRow key={i} label={type.replace(/_/g, ' ')} value={count} max={max} color={INFO} />
            ));
          })()}
        </View>
      </View>
    );
  };

  // ── Drilldown modal ─────────────────────────────────────────────────────
  const renderDrilldownModal = () => (
    <Modal visible={!!drilldownRows} animationType="slide" presentationStyle="pageSheet"
      onRequestClose={closeDrilldown}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#FFF' }}>
        <View style={st.modalH}>
          <View style={{ flex: 1 }}>
            <Text style={st.modalTitle}>{drilldownTitle}</Text>
            <Text style={{ fontSize: 12, color: NEUTRAL, marginTop: 2 }}>
              {drilldownRows?.length || 0} record{drilldownRows?.length === 1 ? '' : 's'}
            </Text>
          </View>
          <TouchableOpacity style={st.modalClose} onPress={closeDrilldown}>
            <MaterialCommunityIcons name="close" size={22} color={NEUTRAL} />
          </TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
          {(drilldownRows || []).length === 0 ? (
            <View style={st.empty}>
              <MaterialCommunityIcons name="database-off" size={40} color="#D1D5DB" />
              <Text style={st.emptyTxt}>No records in this period</Text>
            </View>
          ) : (
            (drilldownRows || []).slice(0, 100).map((item, i) => {
              const name = item.name || item.title || item.candidate_name || item.client_name
                || item.invoice_number || `Record #${item.id}`;
              const amount = parseFloat(item.total || item.value || item.amount || item.salary || 0);
              const dateStr = pickDate(item);
              return (
                <TouchableOpacity key={i} style={st.card}
                  onPress={() => { setSelected(item); setShowDetailModal(true); }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <View style={{ flex: 1 }}>
                      <Text style={st.cardName} numberOfLines={1}>{name}</Text>
                      <Text style={st.cardMeta} numberOfLines={1}>
                        {[item.status, item.stage, item.department, dateStr && formatDate(dateStr)]
                          .filter(Boolean).join(' · ')}
                      </Text>
                    </View>
                    {amount > 0 && (
                      <Text style={st.cardVal}>{formatCurrency(amount)}</Text>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })
          )}
          {(drilldownRows || []).length > 100 && (
            <Text style={{ textAlign: 'center', fontSize: 11, color: NEUTRAL, marginTop: 10 }}>
              Showing first 100 of {drilldownRows.length} records
            </Text>
          )}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Report modal (full chart for one of the reports) ────────────────────
  const renderReportModal = () => (
    <Modal visible={showReportModal} animationType="slide" presentationStyle="pageSheet"
      onRequestClose={() => setShowReportModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#FFF' }}>
        <View style={st.modalH}>
          <Text style={st.modalTitle}>{(activeReport || '').replace(/^./, c => c.toUpperCase())} Report</Text>
          <TouchableOpacity style={st.modalClose} onPress={() => setShowReportModal(false)}>
            <MaterialCommunityIcons name="close" size={22} color={NEUTRAL} />
          </TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
          {activeReport === 'revenue' && (() => {
            const maxMonth = Math.max(1, ...reports.revenueByMonth.months.map(m => m.value));
            return (
              <View>
                <Text style={st.reportSectionTitle}>Revenue by Month</Text>
                <Text style={{ fontSize: 24, fontWeight: '800', color: SUCCESS, marginVertical: 8 }}>
                  {formatCurrency(reports.revenueByMonth.total)}
                </Text>
                <Text style={{ fontSize: 12, color: NEUTRAL, marginBottom: 14 }}>
                  Total revenue over the last 12 months
                </Text>
                {reports.revenueByMonth.months.map((m, i) => (
                  <BarRow key={i} label={m.label} value={m.value} max={maxMonth} color={SUCCESS}
                    suffix="" />
                ))}
              </View>
            );
          })()}

          {activeReport === 'pipeline' && (() => {
            const max = Math.max(1, ...reports.pipelineFunnel.map(s => s.count));
            const totalValue = reports.pipelineFunnel.reduce((s, x) => s + x.value, 0);
            return (
              <View>
                <Text style={st.reportSectionTitle}>Sales Pipeline by Stage</Text>
                <Text style={{ fontSize: 24, fontWeight: '800', color: PRIMARY, marginVertical: 8 }}>
                  {formatCurrency(totalValue)}
                </Text>
                <Text style={{ fontSize: 12, color: NEUTRAL, marginBottom: 14 }}>
                  Total value across all deals
                </Text>
                {reports.pipelineFunnel.length === 0
                  ? <Text style={st.emptyTxt}>No deals tracked</Text>
                  : reports.pipelineFunnel.map((s, i) => (
                      <View key={i}>
                        <FunnelStage label={s.label} value={s.count} max={max} color={PRIMARY} />
                        <Text style={{ fontSize: 11, color: NEUTRAL, marginTop: -4, marginBottom: 6, marginLeft: 4 }}>
                          {formatCurrency(s.value)}
                        </Text>
                      </View>
                    ))}
              </View>
            );
          })()}

          {activeReport === 'recruitment' && (() => {
            const max = Math.max(1, ...reports.recFunnel.map(s => s.value));
            return (
              <View>
                <Text style={st.reportSectionTitle}>Hiring Funnel</Text>
                <Text style={{ fontSize: 24, fontWeight: '800', color: ACCENT, marginVertical: 8 }}>
                  {reports.recFunnel[4].value} hires
                </Text>
                <Text style={{ fontSize: 12, color: NEUTRAL, marginBottom: 14 }}>
                  Conversion through each stage
                </Text>
                {reports.recFunnel.map((s, i) => (
                  <FunnelStage key={i} label={s.label} value={s.value} max={max} color={s.color} indent={i * 6} />
                ))}
                {reports.recFunnel[1].value > 0 && (
                  <View style={{ marginTop: 16, padding: 12, backgroundColor: ACCENT + '10', borderRadius: 10 }}>
                    <Text style={{ fontSize: 12, color: '#5B21B6', fontWeight: '700' }}>
                      Application → Hire rate: {((reports.recFunnel[4].value / reports.recFunnel[1].value) * 100).toFixed(1)}%
                    </Text>
                  </View>
                )}
              </View>
            );
          })()}

          {activeReport === 'topClients' && (() => {
            const max = reports.topClients[0]?.value || 1;
            return (
              <View>
                <Text style={st.reportSectionTitle}>Top Clients by Revenue</Text>
                {reports.topClients.length === 0
                  ? <Text style={st.emptyTxt}>No paid invoices yet</Text>
                  : reports.topClients.map((c, i) => (
                      <View key={i} style={{ marginBottom: 12 }}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text style={{ fontSize: 13, color: '#1F2937', fontWeight: '600' }} numberOfLines={1}>
                            #{i + 1} {c.name}
                          </Text>
                          <Text style={{ fontSize: 13, color: SUCCESS, fontWeight: '700' }}>
                            {formatCurrency(c.value)}
                          </Text>
                        </View>
                        <View style={{ height: 6, backgroundColor: BORDER, borderRadius: 3, overflow: 'hidden' }}>
                          <View style={{
                            width: `${(c.value / max) * 100}%`,
                            height: '100%', backgroundColor: SUCCESS, borderRadius: 3,
                          }} />
                        </View>
                      </View>
                    ))}
              </View>
            );
          })()}

          {activeReport === 'aging' && (() => {
            const buckets = ['current', '0-30', '31-60', '61-90', '90+'];
            const colors = [SUCCESS, INFO, WARNING, '#EA580C', DANGER];
            const totalUnpaid = Object.values(reports.aging.values).reduce((s, v) => s + v, 0);
            const max = Math.max(1, ...Object.values(reports.aging.values));
            return (
              <View>
                <Text style={st.reportSectionTitle}>Invoice Aging</Text>
                <Text style={{ fontSize: 24, fontWeight: '800', color: WARNING, marginVertical: 8 }}>
                  {formatCurrency(totalUnpaid)}
                </Text>
                <Text style={{ fontSize: 12, color: NEUTRAL, marginBottom: 14 }}>
                  Total unpaid across {Object.values(reports.aging.counts).reduce((s, n) => s + n, 0)} invoices
                </Text>
                {buckets.map((b, i) => (
                  <View key={b} style={{ marginBottom: 10 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Text style={{ fontSize: 13, color: '#1F2937', fontWeight: '600' }}>
                        {b === 'current' ? 'Not Due' : `${b} days overdue`}
                      </Text>
                      <Text style={{ fontSize: 12, color: NEUTRAL }}>
                        {reports.aging.counts[b]} invoice{reports.aging.counts[b] === 1 ? '' : 's'}
                      </Text>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <View style={{ flex: 1, height: 10, backgroundColor: BORDER, borderRadius: 5, overflow: 'hidden' }}>
                        <View style={{
                          width: `${(reports.aging.values[b] / max) * 100}%`,
                          height: '100%', backgroundColor: colors[i],
                        }} />
                      </View>
                      <Text style={{ fontSize: 12, fontWeight: '700', color: colors[i], width: 80, textAlign: 'right' }}>
                        {formatCurrency(reports.aging.values[b])}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            );
          })()}

          {activeReport === 'departments' && (() => {
            const max = reports.deptList[0]?.value || 1;
            return (
              <View>
                <Text style={st.reportSectionTitle}>Headcount by Department</Text>
                <Text style={{ fontSize: 24, fontWeight: '800', color: INFO, marginVertical: 8 }}>
                  {data.employees.length} employees
                </Text>
                <Text style={{ fontSize: 12, color: NEUTRAL, marginBottom: 14 }}>
                  Across {reports.deptList.length} department{reports.deptList.length === 1 ? '' : 's'}
                </Text>
                {reports.deptList.length === 0
                  ? <Text style={st.emptyTxt}>No department data</Text>
                  : reports.deptList.map((d, i) => (
                      <BarRow key={i} label={d.name} value={d.value} max={max} color={INFO} />
                    ))}
              </View>
            );
          })()}

          {activeReport === 'contracts' && (() => {
            const cs = reports.contractStatus;
            const total = cs.active + cs.expiring + cs.expired + cs.draft;
            const buckets = [
              { key: 'active',   label: 'Active',   value: cs.active,   color: SUCCESS },
              { key: 'expiring', label: 'Expiring', value: cs.expiring, color: WARNING },
              { key: 'expired',  label: 'Expired',  value: cs.expired,  color: DANGER },
              { key: 'draft',    label: 'Draft',    value: cs.draft,    color: NEUTRAL },
            ];
            const max = Math.max(1, ...buckets.map(b => b.value));
            return (
              <View>
                <Text style={st.reportSectionTitle}>Contract Status</Text>
                <Text style={{ fontSize: 24, fontWeight: '800', color: PRIMARY, marginVertical: 8 }}>
                  {total} contract{total === 1 ? '' : 's'}
                </Text>
                {buckets.map(b => (
                  <BarRow key={b.key} label={b.label} value={b.value} max={max} color={b.color} />
                ))}
              </View>
            );
          })()}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Detail modal (one record) ────────────────────────────────────────────
  const renderDetailModal = () => (
    <Modal visible={showDetailModal} animationType="slide" presentationStyle="pageSheet"
      onRequestClose={() => setShowDetailModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#FFF' }}>
        <View style={st.modalH}>
          <Text style={st.modalTitle} numberOfLines={1}>
            {selected?.name || selected?.title || selected?.candidate_name || selected?.invoice_number || 'Details'}
          </Text>
          <TouchableOpacity style={st.modalClose} onPress={() => setShowDetailModal(false)}>
            <MaterialCommunityIcons name="close" size={22} color={NEUTRAL} />
          </TouchableOpacity>
        </View>
        {selected && (
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
            <View style={st.mSec}>
              <Text style={st.mSecTitle}>Details</Text>
              {Object.entries(selected)
                .filter(([k, v]) => typeof v !== 'object' && v != null && !['id', '_type', '_displayName'].includes(k))
                .slice(0, 20)
                .map(([k, v], i) => (
                  <View key={i} style={{ flexDirection: 'row', paddingVertical: 6,
                    borderBottomWidth: 1, borderBottomColor: '#F3F4F6' }}>
                    <Text style={{ fontSize: 12, color: NEUTRAL, width: 120, fontWeight: '600' }}>
                      {k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </Text>
                    <Text style={{ fontSize: 13, color: '#1F2937', flex: 1 }} numberOfLines={3}>
                      {String(v).substring(0, 200)}
                    </Text>
                  </View>
                ))}
            </View>
            {companyProfile && (
              <View style={st.mSec}>
                <Text style={st.mSecTitle}>Company</Text>
                <Text style={st.mTxt}>
                  {companyProfile.name}{companyProfile.industry ? ` · ${companyProfile.industry}` : ''}
                </Text>
              </View>
            )}
          </ScrollView>
        )}
      </SafeAreaView>
    </Modal>
  );

  // ── Goals modal (edit targets) ──────────────────────────────────────────
  const renderGoalsModal = () => (
    <Modal visible={showGoalsModal} animationType="slide" presentationStyle="pageSheet"
      onRequestClose={() => setShowGoalsModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#FFF' }}>
        <View style={st.modalH}>
          <Text style={st.modalTitle}>Goals & Targets</Text>
          <TouchableOpacity style={st.modalClose} onPress={() => setShowGoalsModal(false)}>
            <MaterialCommunityIcons name="close" size={22} color={NEUTRAL} />
          </TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
          <Text style={{ fontSize: 13, color: NEUTRAL, marginBottom: 16, lineHeight: 19 }}>
            Set targets per KPI for this period. Progress is shown on every KPI card.
            Targets persist per company on this device.
          </Text>
          {[
            { k: 'revenue',      label: 'Revenue',          type: 'currency', icon: 'cash-multiple' },
            { k: 'pipeline',     label: 'Pipeline Value',   type: 'currency', icon: 'chart-bell-curve-cumulative' },
            { k: 'newClients',   label: 'New Clients',      type: 'number',   icon: 'account-multiple-plus' },
            { k: 'applications', label: 'Applications',     type: 'number',   icon: 'file-document-multiple' },
            { k: 'hires',        label: 'Hires',            type: 'number',   icon: 'account-check' },
            { k: 'invoicesPaid', label: 'Invoices Paid',    type: 'number',   icon: 'receipt' },
          ].map(g => {
            const current = kpis.find(k => k.id === g.k)?.value || 0;
            const target = goals[g.k] || 0;
            const pct = target > 0 ? (current / target) * 100 : 0;
            return (
              <View key={g.k} style={st.goalRow}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                  <View style={[st.kpiIcon, { backgroundColor: PRIMARY + '15' }]}>
                    <MaterialCommunityIcons name={g.icon} size={16} color={PRIMARY} />
                  </View>
                  <Text style={{ fontSize: 14, fontWeight: '700', color: '#1F2937', marginLeft: 10, flex: 1 }}>
                    {g.label}
                  </Text>
                  <DonutProgress value={current} target={target} color={PRIMARY} size={42} />
                </View>
                <Text style={{ fontSize: 11, color: NEUTRAL, marginBottom: 6 }}>
                  Current: {g.type === 'currency' ? formatCurrency(current) : formatNum(current)}
                  {' · '}{Math.round(pct)}% of target
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Text style={{ fontSize: 12, color: NEUTRAL }}>Target:</Text>
                  <TextInput
                    style={st.goalInput}
                    value={String(goals[g.k] || 0)}
                    keyboardType="numeric"
                    onChangeText={(t) => {
                      const n = parseFloat(t.replace(/[^0-9.]/g, '')) || 0;
                      saveGoals({ ...goals, [g.k]: n });
                    }}
                  />
                  {g.type === 'currency' && (
                    <Text style={{ fontSize: 12, color: NEUTRAL }}>
                      {companyProfile?.currency || 'GBP'}
                    </Text>
                  )}
                </View>
              </View>
            );
          })}

          <TouchableOpacity
            onPress={() => Alert.alert('Reset Goals', 'Reset all targets to defaults?', [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Reset', style: 'destructive', onPress: () => saveGoals(DEFAULT_GOALS) },
            ])}
            style={[st.submitBtn, { backgroundColor: DANGER, marginTop: 20 }]}>
            <MaterialCommunityIcons name="refresh" size={18} color="#FFF" />
            <Text style={st.submitBtnTxt}>Reset to Defaults</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Custom period picker ────────────────────────────────────────────────
  const renderPeriodPicker = () => (
    <Modal visible={showPeriodPicker} transparent animationType="fade"
      onRequestClose={() => setShowPeriodPicker(false)}>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', padding: 24 }}>
        <View style={{ backgroundColor: '#FFF', borderRadius: 14, padding: 20 }}>
          <Text style={{ fontSize: 16, fontWeight: '800', color: '#1F2937', marginBottom: 14 }}>
            Custom Date Range
          </Text>
          <Text style={{ fontSize: 12, color: NEUTRAL, marginBottom: 4 }}>Start (YYYY-MM-DD)</Text>
          <TextInput
            style={st.formInput}
            value={customStart || ''}
            onChangeText={setCustomStart}
            placeholder="2025-01-01"
            placeholderTextColor="#9CA3AF"
          />
          <Text style={{ fontSize: 12, color: NEUTRAL, marginBottom: 4, marginTop: 12 }}>End (YYYY-MM-DD)</Text>
          <TextInput
            style={st.formInput}
            value={customEnd || ''}
            onChangeText={setCustomEnd}
            placeholder="2025-12-31"
            placeholderTextColor="#9CA3AF"
          />
          <View style={{ flexDirection: 'row', gap: 10, marginTop: 18 }}>
            <TouchableOpacity onPress={() => setShowPeriodPicker(false)}
              style={[st.submitBtn, { flex: 1, backgroundColor: '#F3F4F6' }]}>
              <Text style={[st.submitBtnTxt, { color: NEUTRAL, marginLeft: 0 }]}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => {
                if (customStart && customEnd) {
                  setPeriod('custom');
                  setShowPeriodPicker(false);
                } else {
                  Alert.alert('Missing Dates', 'Please enter both start and end dates.');
                }
              }}
              style={[st.submitBtn, { flex: 1 }]}>
              <Text style={[st.submitBtnTxt, { marginLeft: 0 }]}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ── Content router ──────────────────────────────────────────────────────
  const renderContent = () => {
    if (searchResults) return renderSearchResults();
    if (activeTab === 'dashboard') return renderDashboard();
    if (activeTab === 'kpis')      return renderKPIsTab();
    if (activeTab === 'reports')   return renderReportsTab();
    if (activeTab === 'trends')    return renderTrendsTab();
    return null;
  };

  // ── Loading ─────────────────────────────────────────────────────────────
  if (isLoading) return (
    <SafeAreaView style={st.loading}>
      <StatusBar barStyle="dark-content" />
      <ActivityIndicator size="large" color={PRIMARY} />
      <Text style={st.loadingTxt}>Loading Business Intelligence…</Text>
    </SafeAreaView>
  );

  // ── Main render ─────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={st.container}>
      <StatusBar barStyle="light-content" />
      {renderHeader()}
      {renderPeriodBar()}
      {renderTabs()}
      {renderSearchBar()}
      <ScrollView
        style={{ flex: 1 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]} />}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 40 }}>
        {renderContent()}
      </ScrollView>
      {renderDetailModal()}
      {renderDrilldownModal()}
      {renderReportModal()}
      {renderGoalsModal()}
      {renderPeriodPicker()}
    </SafeAreaView>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// STYLES
// ════════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: BG },
  loading:   { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: BG },
  loadingTxt:{ fontSize: 15, color: NEUTRAL, marginTop: 16, fontWeight: '500' },

  // Header
  header:    { paddingTop: 8, paddingBottom: 16, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn:      { width: 38, height: 38, borderRadius: 19, backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center', justifyContent: 'center' },
  hTitle:    { fontSize: 19, fontWeight: '800', color: '#FFF' },
  hSub:      { fontSize: 12, marginTop: 2, color: '#DDD6FE' },
  chips:     { flexDirection: 'row', flexWrap: 'wrap', marginTop: 12, gap: 6, alignItems: 'center' },
  chip:      { flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 12, paddingHorizontal: 8, paddingVertical: 4 },
  chipTxt:   { fontSize: 11, marginLeft: 4, color: '#DDD6FE' },

  // Period bar
  periodBar: { backgroundColor: SURFACE, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: BORDER },
  periodChip:{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 6,
    borderRadius: 16, backgroundColor: '#F3F4F6', borderWidth: 1, borderColor: BORDER },

  // Tabs
  tabsRow:   { backgroundColor: SURFACE, borderBottomWidth: 1, borderBottomColor: BORDER },
  tab:       { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 11,
    marginRight: 4, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  activeTab: { borderBottomColor: PRIMARY },
  tabTxt:    { fontSize: 13, color: NEUTRAL, marginLeft: 6, fontWeight: '600' },

  // Search
  searchRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: SURFACE,
    paddingHorizontal: 14, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: BORDER, gap: 8 },
  searchInput:{ flex: 1, fontSize: 14, color: '#1F2937' },

  // Section
  sectionTitle: { fontSize: 14, fontWeight: '800', color: '#1F2937',
    paddingHorizontal: 16, marginTop: 16, marginBottom: 10, letterSpacing: 0.2 },

  // KPI grid
  kpiGrid:   { flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: 12, gap: 8 },
  kpiGridItem:{ width: (width - 32) / 2 },
  kpiCard:   { backgroundColor: SURFACE, borderRadius: 12, padding: 12,
    borderWidth: 1, borderColor: BORDER, minHeight: 120 },
  kpiIcon:   { width: 32, height: 32, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  kpiTitle:  { fontSize: 11, color: NEUTRAL, fontWeight: '600', marginBottom: 2 },
  kpiValue:  { fontSize: 18, fontWeight: '800' },

  // Insight cards
  insightCard:{ backgroundColor: SURFACE, borderRadius: 10, padding: 10, marginBottom: 8,
    borderLeftWidth: 3, flexDirection: 'row', alignItems: 'center', position: 'relative' },
  insightIcon:{ width: 28, height: 28, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  insightText:{ flex: 1, marginLeft: 10, fontSize: 13, color: '#1F2937', lineHeight: 18 },
  severityDot:{ position: 'absolute', top: 8, right: 8, width: 6, height: 6, borderRadius: 3 },

  // Highlight
  highlightCard:{ backgroundColor: SURFACE, borderRadius: 12, padding: 14, borderWidth: 2,
    flexDirection: 'row', alignItems: 'center' },

  // Quick report card
  quickReport:{ backgroundColor: SURFACE, borderRadius: 12, padding: 12, borderWidth: 1, borderColor: BORDER },

  // Report card (Reports tab list)
  reportCard:{ flexDirection: 'row', alignItems: 'center', backgroundColor: SURFACE,
    marginHorizontal: 16, marginBottom: 8, padding: 14, borderRadius: 12,
    borderWidth: 1, borderColor: BORDER },
  reportTitle:{ fontSize: 15, fontWeight: '700', color: '#1F2937' },
  reportSubtitle:{ fontSize: 12, color: NEUTRAL, marginTop: 2 },

  // Trend chart card
  chartCard: { backgroundColor: SURFACE, marginHorizontal: 16, padding: 14, borderRadius: 12,
    borderWidth: 1, borderColor: BORDER, marginBottom: 8 },

  // Generic card (search results, drilldown)
  card:      { backgroundColor: SURFACE, marginBottom: 8, padding: 12, borderRadius: 10,
    borderWidth: 1, borderColor: BORDER },
  cardName:  { fontSize: 14, fontWeight: '700', color: '#1F2937' },
  cardMeta:  { fontSize: 11, color: NEUTRAL, marginTop: 2 },
  cardVal:   { fontSize: 13, fontWeight: '800', color: SUCCESS },
  typeBadge: { width: 44, height: 32, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },

  empty:     { alignItems: 'center', padding: 40 },
  emptyTxt:  { fontSize: 14, color: NEUTRAL, marginTop: 10 },

  // Bar
  barRow:    { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  barLbl:    { fontSize: 12, color: '#1F2937', width: 100, fontWeight: '500' },
  barBg:     { flex: 1, height: 8, backgroundColor: '#F3F4F6', borderRadius: 4,
    marginHorizontal: 10, overflow: 'hidden' },
  barFill:   { height: '100%', backgroundColor: PRIMARY, borderRadius: 4 },
  barCnt:    { fontSize: 12, fontWeight: '700', color: '#1F2937', width: 50, textAlign: 'right' },

  // Modal
  modalH:    { flexDirection: 'row', alignItems: 'center', padding: 16,
    borderBottomWidth: 1, borderBottomColor: BORDER },
  modalTitle:{ fontSize: 17, fontWeight: '800', color: '#1F2937', flex: 1 },
  modalClose:{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#F3F4F6',
    alignItems: 'center', justifyContent: 'center' },
  mSec:      { marginBottom: 22 },
  mSecTitle: { fontSize: 14, fontWeight: '800', color: '#1F2937', marginBottom: 10 },
  mTxt:      { fontSize: 13, color: NEUTRAL, lineHeight: 20 },
  reportSectionTitle: { fontSize: 16, fontWeight: '800', color: '#1F2937', marginBottom: 6 },

  // Form
  formField: { marginBottom: 14 },
  formLbl:   { fontSize: 13, fontWeight: '600', color: '#1F2937', marginBottom: 6 },
  formInput: { backgroundColor: '#F9FAFB', borderRadius: 10, padding: 12,
    fontSize: 14, color: '#1F2937', borderWidth: 1, borderColor: BORDER },
  submitBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: PRIMARY, borderRadius: 10, padding: 14 },
  submitBtnTxt: { color: '#FFF', fontWeight: '700', fontSize: 15, marginLeft: 8 },

  // Goals
  goalRow:   { backgroundColor: '#F9FAFB', borderRadius: 12, padding: 12, marginBottom: 10,
    borderWidth: 1, borderColor: BORDER },
  goalInput: { flex: 1, backgroundColor: SURFACE, borderRadius: 8, paddingHorizontal: 10,
    paddingVertical: 8, fontSize: 14, color: '#1F2937', borderWidth: 1, borderColor: BORDER },
});
